import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useWorkspace } from './WorkspaceContext';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const QuotaContext = createContext();

export const useQuota = () => {
    const context = useContext(QuotaContext);
    if (!context) {
        throw new Error('useQuota must be used within a QuotaProvider');
    }
    return context;
};

export const QuotaProvider = ({ children }) => {
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const { idToken } = useWorkspace();
    const loadingRef = useRef(false);

    // Function to refresh quota data
    const refreshQuota = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Function to fetch quota data
    const fetchQuota = useCallback(async (quotaTypes = {}) => {
        if (!idToken || loadingRef.current) return null;
        
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        
        try {
            const newQuota = {};
            
            // Fetch GPT and image quota with a single API call
            if (quotaTypes.showGpt || quotaTypes.showImage) {
                try {
                    const aiQuotaResponse = await axios.get(`${BACKEND_API_URL}/api/quota/ai-quota`, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    });
                    
                    if (aiQuotaResponse.data.success) {
                        // Process GPT quota
                        if (quotaTypes.showGpt) {
                            newQuota.gpt = aiQuotaResponse.data.quota;
                        }
                        
                        // Process image quota
                        if (quotaTypes.showImage) {
                            newQuota.image = aiQuotaResponse.data.imageQuota;
                        }
                    }
                } catch (err) {
                    console.error('Error fetching AI quota:', err);
                    
                    // Fallback for GPT quota
                    if (quotaTypes.showGpt) {
                        newQuota.gpt = {
                            plan: 'free',
                            limit: 50,
                            used: 0,
                            remaining: 50,
                            unlimited: false
                        };
                    }
                    
                    // Fallback for image quota
                    if (quotaTypes.showImage) {
                        newQuota.image = {
                            plan: 'free',
                            limit: 5,
                            used: 0,
                            remaining: 5,
                            unlimited: false
                        };
                    }
                }
            }
            
            // Fetch Upload quota
            if (quotaTypes.showUpload) {
                try {
                    const uploadResponse = await axios.get(`${BACKEND_API_URL}/api/quota/upload-quota`, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    });
                    
                    if (uploadResponse.data) {
                        const uploadData = uploadResponse.data;
                        newQuota.upload = {
                            plan: uploadData.plan || 'free',
                            limit: uploadData.limit,
                            used: uploadData.uploadCount,
                            remaining: uploadData.limit - uploadData.uploadCount,
                            unlimited: false,
                            resetTime: uploadData.resetTime
                        };
                    }
                } catch (err) {
                    console.error('Error fetching Upload quota:', err);
                    
                    // Fallback for Upload quota
                    newQuota.upload = {
                        plan: 'free',
                        limit: 10,
                        used: 0,
                        remaining: 10,
                        unlimited: false
                    };
                }
            }
            
            // Fetch Twitter quota
            if (quotaTypes.showTwitter) {
                try {
                    const twitterResponse = await axios.get(`${BACKEND_API_URL}/api/quota/twitter-quota`, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    });
                    
                    if (twitterResponse.data.success) {
                        newQuota.twitter = twitterResponse.data.quota;
                    }
                } catch (err) {
                    console.error('Error fetching Twitter quota:', err);
                    
                    // Fallback for Twitter quota
                    newQuota.twitter = {
                        plan: 'free',
                        limit: 1,
                        used: 0,
                        remaining: 1,
                        unlimited: false
                    };
                }
            }
            
            // Update state with all quota data at once
            setQuota(prevQuota => ({
                ...prevQuota,
                ...newQuota
            }));
            
            return newQuota;
            
        } catch (err) {
            console.error('Error fetching quota:', err);
            setError('Failed to load quota information');
            return null;
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [idToken]);

    // Auto-fetch quota when refreshTrigger changes (only for refreshes, not initial load)
    useEffect(() => {
        if (idToken && refreshTrigger > 0) {
            fetchQuota({ showGpt: true, showImage: true, showUpload: true, showTwitter: true });
        }
    }, [refreshTrigger, idToken]); // Stable dependencies only

    const contextValue = {
        quota,
        loading,
        error,
        refreshQuota,
        fetchQuota
    };

    return (
        <QuotaContext.Provider value={contextValue}>
            {children}
        </QuotaContext.Provider>
    );
}; 